import { useNavigate } from 'react-router-dom';
import { useLeaveBalance, useApprovalStats } from '@saiens/shared/hooks';
import { formatWorkHours } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  ArrowLeft,
  Plus,
  List,
  Clock,
  ClipboardList,
  History,
  CalendarDays,
} from 'lucide-react';

export function LeaveHubPage() {
  const navigate = useNavigate();
  const { data: balanceData, isLoading: balanceLoading } = useLeaveBalance();
  const { data: approvalStats } = useApprovalStats();

  return (
    <div className="mx-auto max-w-lg">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center gap-3 bg-white px-4 py-3 shadow-sm">
        <button onClick={() => navigate(-1)} className="rounded-full p-1 hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-semibold">請假及簽核</h1>
      </div>

      <div className="space-y-4 px-4 py-4">
        {/* Balance Section */}
        <div>
          <h2 className="mb-2 text-sm font-semibold text-gray-500">假別餘額</h2>
          {balanceLoading ? (
            <div className="flex h-20 items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : balanceData && balanceData.length > 0 ? (
            <div className="flex gap-3 overflow-x-auto pb-2">
              {balanceData.map((b) => (
                <div
                  key={`${b.type_id}-${b.validity_end}`}
                  className="flex-shrink-0 rounded-xl bg-white p-3 shadow-sm"
                  style={{ minWidth: '140px' }}
                >
                  <p className="text-xs font-medium text-gray-500">{b.type_name}</p>
                  <p className="mt-1 text-lg font-bold text-gray-900">
                    {formatWorkHours(b.remaining_hours)}
                  </p>
                  <p className="text-xs text-gray-400">
                    共 {formatWorkHours(b.total_hours)}
                  </p>
                  {b.validity_end && (
                    <p className="mt-1 text-xs text-gray-400">
                      至 {b.validity_end.substring(5)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">尚無假別分配</p>
          )}
        </div>

        {/* Leave Section */}
        <div>
          <h2 className="mb-2 text-sm font-semibold text-gray-500">請假管理</h2>
          <div className="space-y-2">
            <button
              onClick={() => navigate('/leave/apply')}
              className="flex w-full items-center gap-3 rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
            >
              <div className="rounded-lg bg-pink-50 p-2">
                <Plus className="h-5 w-5 text-pink-600" />
              </div>
              <p className="text-sm font-medium text-gray-900">新增請假</p>
            </button>

            <button
              onClick={() => navigate('/leave')}
              className="flex w-full items-center gap-3 rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
            >
              <div className="rounded-lg bg-pink-50 p-2">
                <List className="h-5 w-5 text-pink-600" />
              </div>
              <p className="text-sm font-medium text-gray-900">請假紀錄</p>
            </button>
          </div>
        </div>

        {/* Overtime Section */}
        <div>
          <h2 className="mb-2 text-sm font-semibold text-gray-500">加班管理</h2>
          <div className="space-y-2">
            <button
              onClick={() => navigate('/overtime/apply')}
              className="flex w-full items-center gap-3 rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
            >
              <div className="rounded-lg bg-amber-50 p-2">
                <Plus className="h-5 w-5 text-amber-600" />
              </div>
              <p className="text-sm font-medium text-gray-900">加班申請</p>
            </button>

            <button
              onClick={() => navigate('/overtime')}
              className="flex w-full items-center gap-3 rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
            >
              <div className="rounded-lg bg-amber-50 p-2">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <p className="text-sm font-medium text-gray-900">加班紀錄</p>
            </button>
          </div>
        </div>

        {/* Allocation Section */}
        <div>
          <h2 className="mb-2 text-sm font-semibold text-gray-500">分配管理</h2>
          <button
            onClick={() => navigate('/allocation')}
            className="flex w-full items-center gap-3 rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
          >
            <div className="rounded-lg bg-violet-50 p-2">
              <CalendarDays className="h-5 w-5 text-violet-600" />
            </div>
            <p className="text-sm font-medium text-gray-900">分配紀錄</p>
          </button>
        </div>

        {/* Approval Section */}
        <div>
          <h2 className="mb-2 text-sm font-semibold text-gray-500">簽核管理</h2>
          <div className="space-y-2">
            <button
              onClick={() => navigate('/approval/pending')}
              className="flex w-full items-center justify-between rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-red-50 p-2">
                  <ClipboardList className="h-5 w-5 text-red-600" />
                </div>
                <p className="text-sm font-medium text-gray-900">待辦簽核</p>
              </div>
              {approvalStats && approvalStats.pending_count > 0 && (
                <span className="rounded-full bg-red-500 px-2.5 py-0.5 text-xs font-semibold text-white">
                  {approvalStats.pending_count}
                </span>
              )}
            </button>

            <button
              onClick={() => navigate('/approval/history')}
              className="flex w-full items-center gap-3 rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
            >
              <div className="rounded-lg bg-blue-50 p-2">
                <History className="h-5 w-5 text-blue-600" />
              </div>
              <p className="text-sm font-medium text-gray-900">簽核歷史</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
